import { useCallback, useEffect, useMemo, useRef } from 'react'

import { logger } from '../../logging/logger'
import {
  type SystemColorScheme,
  useColorScheme,
} from '../../theme/useColorScheme'
import type { StripeOnrampClient } from './stripeOnramp'
import type { StripeSdk } from './stripeSdk'

/**
 * What a host must supply to turn Stripe on: its own Stripe account's
 * publishable key, and the Apple Merchant ID baked into the app's entitlements.
 * Data only — the host installs `@stripe/stripe-react-native` (an optional peer)
 * and this package binds it (see {@link useStripeOnrampClient}), so a host wires
 * three strings, not an implementation.
 */
export interface StripeOnrampConfig {
  /** Publishable key for the Stripe account the onramp settles into. */
  publishableKey: string
  /** Shown on Stripe's own sheets. Defaults to the app's display name. */
  merchantDisplayName: string
  /**
   * Apple Merchant ID (`merchant.example.app`). Required for Apple Pay: it is
   * an app entitlement, so only the host knows it, and Stripe decrypts the
   * payment against the certificate bound to it.
   */
  merchantIdentifier?: string
  /** Deep-link scheme Stripe returns to after a redirect (3DS, KYC). */
  urlScheme?: string
}

/**
 * Binds Stripe's native SDK to the {@link StripeOnrampClient} the fiat surfaces
 * call. Takes the SDK as an argument because it is an optional peer, loaded by
 * `StripeOnrampBridge` only after a host configured Stripe.
 *
 * `initStripe` is idempotent per config but not free, so it runs once per
 * distinct config and every call awaits that same promise — the SDK rejects
 * calls made before it resolves. A failed init clears the cache so the next
 * call retries rather than latching the failure forever.
 */
export function useStripeOnrampClient(
  { initStripe, isPlatformPaySupported, useOnramp }: StripeSdk,
  config: StripeOnrampConfig,
): StripeOnrampClient {
  const {
    attachKycInfo,
    authenticateUserWithToken,
    authorize,
    collectPaymentMethod,
    configure,
    createCryptoPaymentToken,
    hasLinkAccount,
    logOut,
    performCheckout,
    presentKycInfoVerification,
    registerLinkUser,
    registerWalletAddress,
    verifyIdentity,
  } = useOnramp()
  const scheme = useColorScheme()
  // The four strings, not the object: the documented wiring passes an inline
  // literal to `FunkitProvider`, so identity changes on every host render and
  // `initStripe` + `configure` ran again each time.
  const configKey = [
    config.publishableKey,
    config.merchantDisplayName,
    config.merchantIdentifier ?? '',
    config.urlScheme ?? '',
  ].join('\u0000')
  // Keyed on the scheme as well: `configure` is what carries the appearance, so a
  // scheme change with the same config has to re-run it or Stripe's sheets keep the old one.
  const readyRef = useRef<
    | {
        key: string
        scheme: SystemColorScheme
        promise: Promise<void>
      }
    | undefined
  >(undefined)

  const ensureReady = useCallback(() => {
    if (
      readyRef.current?.key !== configKey ||
      readyRef.current.scheme !== scheme
    ) {
      const promise = initStripe({
        publishableKey: config.publishableKey,
        merchantIdentifier: config.merchantIdentifier,
        urlScheme: config.urlScheme,
      })
        .then(async () => {
          const configured = await configure({
            merchantDisplayName: config.merchantDisplayName,
            appearance: {
              style: scheme === 'dark' ? 'ALWAYS_DARK' : 'ALWAYS_LIGHT',
            },
          })
          if (configured.error !== undefined) {
            throw configured.error
          }
        })
        .catch((error: unknown) => {
          if (readyRef.current?.promise === promise) {
            readyRef.current = undefined
          }
          throw error
        })
      readyRef.current = { key: configKey, scheme, promise }
      return promise
    }
    return readyRef.current.promise
  }, [config, configKey, configure, initStripe, scheme])

  useEffect(() => {
    void ensureReady().catch((error: unknown) => {
      // Every client call awaits the same promise and surfaces the failure
      // where it can be shown; this is the only place a host sees it early.
      // The usual cause is the Onramp module not linked, which Stripe's own
      // message names.
      logger.warn('connect-rn:stripe_init_failed', {
        error: error instanceof Error ? error.message : String(error),
      })
    })
  }, [ensureReady])

  return useMemo<StripeOnrampClient>(
    () => ({
      hasLinkAccount: async (email) => {
        await ensureReady()
        return hasLinkAccount(email)
      },
      registerLinkUser: async (registration) => {
        await ensureReady()
        return registerLinkUser(registration)
      },
      authorize: async (linkAuthIntentId) => {
        await ensureReady()
        return authorize(linkAuthIntentId)
      },
      authenticateUserWithToken: async (linkAuthTokenClientSecret) => {
        await ensureReady()
        return authenticateUserWithToken(linkAuthTokenClientSecret)
      },
      attachKycInfo: async (info) => {
        await ensureReady()
        return attachKycInfo(info)
      },
      presentKycInfoVerification: async (updatedAddress) => {
        await ensureReady()
        return presentKycInfoVerification(updatedAddress as never)
      },
      verifyIdentity: async () => {
        await ensureReady()
        return verifyIdentity()
      },
      isPlatformPaySupported: async () => {
        await ensureReady()
        return isPlatformPaySupported()
      },
      logOut: async () => {
        await ensureReady()
        return logOut()
      },
      registerWalletAddress: async (walletAddress, network) => {
        await ensureReady()
        return registerWalletAddress(walletAddress, network as never)
      },
      collectPaymentMethod: async (paymentMethod, params) => {
        await ensureReady()
        return collectPaymentMethod(paymentMethod, params as never)
      },
      createCryptoPaymentToken: async () => {
        await ensureReady()
        return createCryptoPaymentToken()
      },
      performCheckout: async (onrampSessionId, provideClientSecret) => {
        await ensureReady()
        return performCheckout(onrampSessionId, provideClientSecret)
      },
    }),
    [
      attachKycInfo,
      authenticateUserWithToken,
      authorize,
      collectPaymentMethod,
      createCryptoPaymentToken,
      ensureReady,
      hasLinkAccount,
      isPlatformPaySupported,
      logOut,
      performCheckout,
      presentKycInfoVerification,
      registerLinkUser,
      registerWalletAddress,
      verifyIdentity,
    ],
  )
}
